import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { db } from "../../firebase";
import {
  collection, doc, getDoc, getDocs,
  query, where,
} from "firebase/firestore";
import { Avatar, Stars, IC, VerifiedBadge } from "../shared";
import { AllReviews, RolePill } from "./AllReviews";

// ── User Profile Bottom Sheet ────────────────────────────────────────────────
// Opens as a bottom sheet (same animation as TripDetails) showing public profile
// with stats, reviews, and a report button.

const UserProfileSheet = ({ userId, onClose }) => {
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      // Fetch user doc
      const userSnap = await getDoc(doc(db, "users", userId));
      if (userSnap.exists()) {
        setUser({ id: userSnap.id, ...userSnap.data() });
      }
      // Fetch reviews (ratings where this user was rated)
      const ratingsSnap = await getDocs(
        query(collection(db, "ratings"), where("ratedId", "==", userId))
      );
      const items = [];
      for (const d of ratingsSnap.docs) {
        const r = d.data();
        const raterSnap = await getDoc(doc(db, "users", r.raterId));
        const rater = raterSnap.exists() ? raterSnap.data() : null;
        // Determine role: if ratedAs exists use it, otherwise check the trip
        let role = r.ratedAs || null;
        if (!role && r.tripId) {
          const tripSnap = await getDoc(doc(db, "trips", r.tripId));
          if (tripSnap.exists()) {
            role = tripSnap.data().driverId === userId ? "driver" : "passenger";
          }
        }
        items.push({
          id: d.id,
          score: r.score,
          comment: r.comment || null,
          ratedAs: role,
          raterId: r.raterId,
          raterName: rater?.name || "Unknown",
          raterAvatar: rater?.avatar || null,
          ratedAt: r.ratedAt,
        });
      }
      items.sort((a, b) => (b.ratedAt?.seconds || 0) - (a.ratedAt?.seconds || 0));
      setReviews(items);
      setLoading(false);
    })();
  }, [userId]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose(), 350);
  };

  if (!userId) return null;

  const memberSince = user?.createdAt?.toDate
    ? user.createdAt.toDate().toLocaleDateString("en", { month: "long", year: "numeric" })
    : user?.createdAt
      ? new Date(user.createdAt).toLocaleDateString("en", { month: "long", year: "numeric" })
      : null;

  const avgRating = user?.rating || 0;
  const reviewCount = user?.ratingCount || 0;
  const sinceYear = user?.createdAt?.toDate
    ? user.createdAt.toDate().getFullYear()
    : user?.createdAt ? new Date(user.createdAt).getFullYear() : "—";

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9000 }}>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(15,14,19,0.5)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          opacity: closing ? 0 : 1,
          transition: "opacity .3s ease",
        }}
      />
      {/* Sheet */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        maxHeight: "85%",
        background: "var(--card)",
        borderRadius: "28px 28px 0 0",
        boxShadow: "0 -10px 40px rgba(0,0,0,.15)",
        display: "flex", flexDirection: "column", overflow: "hidden",
        transform: closing ? "translateY(100%)" : "translateY(0)",
        animation: closing ? "none" : "sheetSlideUp .4s cubic-bezier(0.22,0.68,0,1) both",
        transition: closing ? "transform .35s cubic-bezier(0.55,0,1,0.45)" : "none",
      }}>
        {/* Handle + close */}
        <div style={{ flexShrink: 0, padding: "12px 20px 0", position: "relative" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)", margin: "0 auto 8px" }}/>
          <button onClick={handleClose} style={{
            position: "absolute", top: 10, right: 16,
            width: 28, height: 28, borderRadius: "50%",
            background: "var(--paper)", border: "1.5px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 10,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 40px", WebkitOverflowScrolling: "touch" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
              <span style={{ width: 28, height: 28, border: "3px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>
            </div>
          ) : !user ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "var(--muted)" }}>
              <div style={{ fontSize: 40 }}>👤</div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, marginTop: 10 }}>User not found</div>
            </div>
          ) : (
            <>
              {/* Avatar */}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                <div style={{ border: "3px solid var(--card)", borderRadius: "50%", boxShadow: "var(--shadow-lg)" }}>
                  <Avatar user={user} size={70}/>
                </div>
              </div>

              {/* Name + badge + member since */}
              <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--ink)" }}>{user.name}</span>
                  {avgRating >= 4.5 && <VerifiedBadge size={18}/>}
                </div>
                {memberSince && (
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>Member since {memberSince}</div>
                )}
              </div>

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 16 }}>
                {[
                  { label: "Trips", val: reviewCount || 0 },
                  { label: "Rating", val: avgRating ? avgRating.toFixed(1) : "—" },
                  { label: "Since", val: sinceYear },
                ].map((s, i) => (
                  <div key={i} style={{
                    background: "var(--paper)", borderRadius: 16, padding: "14px 8px",
                    textAlign: "center", border: "1.5px solid var(--border)",
                  }}>
                    <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--accent)" }}>{s.val}</div>
                  </div>
                ))}
              </div>

              {/* Reviews section */}
              <div style={{ marginTop: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>Reviews</div>
                  {reviews.length > 0 && (
                    <span style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--accent)", background: "rgba(43,91,224,.08)", padding: "3px 10px", borderRadius: 99, border: "1px solid rgba(43,91,224,.15)" }}>{reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
                  )}
                </div>

                {/* Summary card */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
                  padding: 16, background: "var(--paper)", borderRadius: 16, border: "1.5px solid var(--border)",
                }}>
                  <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 36, color: "var(--accent)" }}>
                    {avgRating ? avgRating.toFixed(1) : "—"}
                  </div>
                  <div>
                    <Stars rating={avgRating} size={16}/>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{reviewCount} review{reviewCount !== 1 ? "s" : ""}</div>
                  </div>
                </div>

                {/* Show only first 5 reviews */}
                {reviews.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px 0", color: "var(--muted)" }}>
                    <div style={{ fontSize: 13 }}>No reviews yet</div>
                  </div>
                ) : (
                  <>
                    {reviews.slice(0, 5).map(r => (
                      <div key={r.id} style={{
                        display: "flex", gap: 12, padding: "14px 0",
                        borderBottom: "1px solid var(--border)",
                      }}>
                        <Avatar user={{ id: r.raterId, name: r.raterName, avatar: r.raterAvatar }} size={38}/>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{r.raterName}</div>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>
                              {r.ratedAt?.toDate
                                ? r.ratedAt.toDate().toLocaleDateString("en", { month: "short", day: "numeric" })
                                : ""}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Stars rating={r.score} size={12}/>
                            <RolePill role={r.ratedAs}/>
                          </div>
                          {r.comment && (
                            <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.4, marginTop: 4 }}>{r.comment}</div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* See All Reviews button */}
                    {reviews.length > 5 && (
                      <button onClick={() => setShowAllReviews(true)} style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        width: "100%", padding: 13, marginTop: 8,
                        borderRadius: 14, border: "1.5px solid var(--border)",
                        background: "var(--paper)", color: "var(--accent)",
                        fontFamily: "Syne", fontWeight: 700, fontSize: 13,
                        cursor: "pointer", transition: "all .15s",
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h10"/></svg>
                        See All {reviews.length} Reviews
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* All Reviews full screen */}
              {showAllReviews && (
                <AllReviews
                  reviews={reviews}
                  avgRating={avgRating}
                  reviewCount={reviewCount}
                  userName={user.name}
                  onClose={() => setShowAllReviews(false)}
                />
              )}

              {/* Report User button */}
              <button style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                width: "100%", padding: 14, marginTop: 20,
                borderRadius: 14, border: "1px solid var(--danger)",
                background: "rgba(192,57,43,0.08)", color: "var(--danger)",
                fontFamily: "Syne", fontWeight: 700, fontSize: 14,
                cursor: "pointer", transition: "background .15s",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                Report User
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserProfileSheet;
